import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ModalService } from '../../services/modal.service';
import { ServiceMedia, ServiceProviderService } from '../../services/service-provider.service';

// Reusable View/Upload buttons for media scoped to a catalogue product (an "item").
// productId is a product's `_meta.id` (unique within its provider). Built so it can be
// dropped into the product-detail panel now and line-item rows later.
//
//   <app-item-media [productId]="prod._meta.id"></app-item-media>
@Component({
  selector: 'app-item-media',
  templateUrl: './item-media.component.html',
  // In iconOnly mode the host renders bare <button>s as siblings. Make the
  // host itself an inline-flex so the two icon buttons stay together on one
  // line and behave as a single unit inside the parent action-cell flex.
  styles: [
    `:host { display: inline-flex; align-items: center; gap: 0.5rem; }`,
  ],
})
export class ItemMediaComponent implements OnChanges {
  // A product is identified by its provider (numeric) + a composite sub-key
  // "<serviceMetaId>:<productMetaId>" (products have no numeric id).
  @Input() serviceProviderId!: number | string | null | undefined;
  @Input() productSubKey!: string | null | undefined;
  @Input() compact = false; // smaller buttons for line-item rows
  @Input() viewOnly = false; // hide the Upload action (e.g. arrangement product-detail panel)
  @Input() iconOnly = false; // round icon buttons with tooltips (table action cells); implies viewOnly

  // Eagerly loaded in iconOnly mode so the disabled state is correct before the
  // user hovers — and so the tooltip can show the count.
  docCount = 0;
  imageCount = 0;

  constructor(
    private modalService: ModalService,
    private serviceProviderService: ServiceProviderService,
    private messageService: MessageService
  ) {}

  get ready(): boolean {
    return this.serviceProviderId != null && !!this.productSubKey;
  }

  private get providerId(): number {
    return Number(this.serviceProviderId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Counts only matter in iconOnly mode (drives disabled state + tooltip).
    if (!this.iconOnly) return;
    if (!('serviceProviderId' in changes) && !('productSubKey' in changes)) return;
    this.docCount = 0;
    this.imageCount = 0;
    if (!this.ready) return;
    // Single un-kinded fetch; partition client-side. Silent on error — this is
    // a passive affordance, not a primary action.
    this.serviceProviderService.getItemMedia(this.providerId, this.productSubKey!).subscribe({
      next: (items: ServiceMedia[]) => {
        this.docCount = items.filter((i) => i.kind === 'document').length;
        this.imageCount = items.filter((i) => i.kind === 'image').length;
      },
      error: () => undefined,
    });
  }

  openView(kind: 'document' | 'image'): void {
    if (!this.ready) return;
    this.serviceProviderService.getItemMedia(this.providerId, this.productSubKey!, kind).subscribe({
      next: (items) =>
        this.modalService.mediaViewer({
          kind,
          items,
          title: kind === 'image' ? 'Item images' : 'Item documents',
          emptyMessage:
            kind === 'image' ? 'No images uploaded yet.' : 'No documents uploaded yet.',
        }),
      error: () =>
        this.messageService.add({
          severity: 'error',
          summary: 'Could not load media',
          detail: 'Please try again.',
        }),
    });
  }

  // Arrow-fn wrappers for PrimeNG MenuItem.command (preserves `this`).
  uploadDocuments = (): void => this.openUpload('document');
  uploadImages = (): void => this.openUpload('image');

  openUpload(kind: 'document' | 'image'): void {
    if (!this.ready) return;
    const ref = this.modalService.fileUpload({
      title: kind === 'image' ? 'Upload images' : 'Upload documents',
      bucketPrefix: kind === 'image' ? 'service-item-images' : 'service-item-documents',
      accept: kind === 'image' ? 'image/*' : '*',
      multiple: true,
    });
    ref.onClose.subscribe((result?: { files: { id: number }[] }) => {
      if (!result?.files?.length) return;
      const fileIds = result.files.map((f) => f.id);
      this.serviceProviderService.addItemMedia(this.providerId, this.productSubKey!, fileIds, kind).subscribe({
        next: () => {
          if (kind === 'image') this.imageCount += fileIds.length;
          else this.docCount += fileIds.length;
          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: `${fileIds.length} ${kind === 'image' ? 'image' : 'document'}(s) added.`,
          });
        },
        error: () =>
          this.messageService.add({
            severity: 'error',
            summary: 'Save failed',
            detail: 'The files uploaded but could not be linked. Please try again.',
          }),
      });
    });
  }
}
